import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const KINDS = ["identity", "business_fact", "person", "recurring_priority", "preference", "note"];

const KIND_LABELS = {
  identity: "IDENTITY",
  business_fact: "BUSINESS",
  person: "PEOPLE",
  recurring_priority: "RECURRING PRIORITIES",
  preference: "PREFERENCES",
  note: "NOTES",
};

const MemoryEditor = () => {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("business_fact");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.assistantMemory().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.addAssistantMemory({ kind, content });
      toast("Memory added");
      setContent("");
      load();
    } catch (e) {
      toast("Could not save", { description: e?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.deleteAssistantMemory(id);
    toast("Memory removed");
    load();
  };

  const grouped = React.useMemo(() => {
    const g = {};
    items.forEach((f) => {
      const k = f.kind || "note";
      if (!g[k]) g[k] = [];
      g[k].push(f);
    });
    return g;
  }, [items]);

  return (
    <section data-testid="memory-editor">
      <SectionHeader
        code="03"
        label="SALLY · MEMORY"
        caption="What she remembers about you, your businesses, your people, your rules."
      />
      <div className="op-panel p-6 md:p-8 space-y-6">
        {/* Add form */}
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-3" data-testid="memory-add-form">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="op-input op-mono text-xs !py-2"
            data-testid="memory-kind"
          >
            {KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
          </select>
          <input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Fact, rule, or preference — plain sentence"
            className="op-input"
            maxLength={500}
            data-testid="memory-content"
          />
          <button
            type="submit"
            disabled={saving || !content.trim()}
            className="op-btn op-btn--primary inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="memory-add"
          >
            <Plus size={12} /> ADD
          </button>
        </form>

        {/* Grouped facts */}
        {KINDS.map((k) => {
          const rows = grouped[k];
          if (!rows || rows.length === 0) return null;
          return (
            <div key={k} className="space-y-2" data-testid={`memory-group-${k}`}>
              <div className="op-microcopy text-[#00E5FF]">{KIND_LABELS[k]}</div>
              <ul className="space-y-1.5">
                {rows.map((f) => (
                  <li
                    key={f.id}
                    data-testid={`memory-row-${f.id}`}
                    className="flex items-start gap-3 group border-l-2 border-l-white/[0.06] pl-3 py-1"
                  >
                    <span className="text-white text-sm flex-1 leading-snug">{f.content}</span>
                    <button
                      onClick={() => del(f.id)}
                      className="text-[#5E657D] hover:text-[#ef4444] opacity-0 group-hover:opacity-100 transition"
                      data-testid={`memory-del-${f.id}`}
                      aria-label="Delete memory"
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="text-[#5E657D] text-sm">No memory yet. Add a fact and she'll learn it.</div>
        )}
      </div>
    </section>
  );
};

export default MemoryEditor;
